import { BaseSeratoFilter } from '@/pixi/filters/BaseSeratoFilter'
import { ColourStop } from '@/static/gradient-maps'
import * as PIXI from 'pixi.js'
import { EffectState } from '@/pixi/filters/types'

interface VintageUniforms {
  gradientMap: PIXI.Texture
  vintageAlpha: number
}

export class VintageFilter extends BaseSeratoFilter {
  get uniforms (): VintageUniforms {
    return super.uniforms as VintageUniforms
  }

  constructor (effectState: EffectState, gradientColourStops: ColourStop[]) {
    const shader = ` 
    // These mimic what you see in Photoshop level editor, range is 0 and 255.
    // inputGamma 1.0 === 128 midtone
    #define inputBlack 0.00
    #define inputGamma 1.000
    #define inputWhite 255.0
    #define outputWhite 255.0
    #define outputBlack 66.0

    #define noiseAlpha 0.08
    #define saturation 0.8

    varying vec2 vTextureCoord;
    uniform sampler2D uSampler;
    
    uniform sampler2D gradientMap;
    uniform float vintageAlpha;

    mat4 saturationMatrix()
    {
        vec3 luminance = vec3( 0.3086, 0.6094, 0.0820 );
        
        float oneMinusSat = 1.0 - saturation;
        
        vec3 red = vec3( luminance.x * oneMinusSat );
        red+= vec3( saturation, 0, 0 );
        
        vec3 green = vec3( luminance.y * oneMinusSat );
        green += vec3( 0, saturation, 0 );
        
        vec3 blue = vec3( luminance.z * oneMinusSat );
        blue += vec3( 0, 0, saturation );
        
        return mat4( red,     0,
                    green,   0,
                    blue,    0,
                    0, 0, 0, 1 );
    }

    float adjustSingleChannelLevel(float channel)
    {
      // Apply the inputs, return value between 0 - 1
      channel = ( channel * 255.0 - inputBlack ) / ( inputWhite - inputBlack );
      // Apply midtone/gamma adjustment
      channel = pow( channel, inputGamma );
      // Apply the outputs return value between 0 - 1
      channel = (channel * ( outputWhite - outputBlack ) + outputBlack) / 255.0;

      return channel;
    }

    vec3 adjustColourLevels(vec3 colour)
    {
        colour.r = adjustSingleChannelLevel(colour.r);
        colour.g = adjustSingleChannelLevel(colour.g);
        colour.b = adjustSingleChannelLevel(colour.b);

        return colour;
    }

    // Book of shaders, uniform distributed random number generator
    float random (vec2 st) {
      return fract(sin(dot(st.xy, vec2(12.9898,78.233))) * 43758.5453123);
    }

    float generateNoise(vec2 n) 
    {
      vec3 colour = texture2D(uSampler, vTextureCoord).rgb;

      float seed = fract( colour.r );
      float nrnd0 = random( n + 0.07 * seed );
      float nrnd1 = random( n + 0.11 * seed );	
      float nrnd2 = random( n + 0.13 * seed );
      float nrnd3 = random( n + 0.17 * seed );
        
      return (nrnd0+nrnd1+nrnd2+nrnd3) / 4.0;
    }

    float overlayBlendChannel(float base, float blend) 
    {
      return base < 0.5 ? 2.0 * base * blend : 1.0 - (2.0 * (1.0 - base) * (1.0 - blend));
    }
  
    vec3 overlayBlend(vec3 base, vec3 blend) 
    {
      vec3 colour;
      colour.r = overlayBlendChannel(base.r, blend.r);
      colour.g = overlayBlendChannel(base.g, blend.g);
      colour.b = overlayBlendChannel(base.b, blend.b);
      return colour;
    }

    vec4 alphaComposite(vec4 base, vec4 blend) 
    {
      vec4 colour;
      colour.a = blend.a + base.a * (1.0 - blend.a);
      colour.r = ((blend.r * blend.a) + base.r * base.a * (1.0 - blend.a)) / (colour.a);
      colour.g = ((blend.g * blend.a) + base.g * base.a * (1.0 - blend.a)) / (colour.a);
      colour.b = ((blend.b * blend.a) + base.b * base.a * (1.0 - blend.a)) / (colour.a);
      return colour;
    }

    void main(void)
    {
      vec4 texturePixelColour = texture2D(uSampler, vTextureCoord);
      
      // Decrease the saturation
      vec4 deSaturatedColour = saturationMatrix() * texturePixelColour;

      vec3 colour = adjustColourLevels(deSaturatedColour.rgb);
      
      // generate noise map and combine with base texture
      float noise = generateNoise(vTextureCoord);
      colour = alphaComposite(vec4(colour,texturePixelColour.a), vec4(vec3(noise), noiseAlpha)).rgb;
      
      // Apply the gradient map
      float greyscale = dot(colour, vec3(0.299, 0.587, 0.114));
      //get the sampled colour from the gradient map
      vec3 mappedColour = texture2D(gradientMap, vec2(greyscale, 1.0)).rgb;
      //mix the gradient colour with the original using overlay blend
      colour = overlayBlend(colour, mappedColour);

      // Alpha blend the original with the vintage
      colour = alphaComposite(texturePixelColour, vec4(colour, vintageAlpha)).rgb;

      // pixel alpha is premultiplied, need to set it to black if transparent to be truely transparent
      gl_FragColor = texturePixelColour.a == 0.0 ? vec4(0.0) : vec4(colour, texturePixelColour.a);
    }`

    const uniforms: VintageUniforms = {
      gradientMap: VintageFilter.createGradientMap(gradientColourStops),
      vintageAlpha: 1.0
    }

    super(
      effectState,
      undefined,
      shader,
      uniforms
    )

  }

  updateUniforms (): void {
    this.uniforms.vintageAlpha = this.effectState.intensity * 0.85 + 0.15
  }

  private static createGradientMap (colourStops: ColourStop[]): PIXI.Texture {
    const gradientCanvas = document.createElement('canvas')
    gradientCanvas.width = 256
    gradientCanvas.height = 1
    const ctx = gradientCanvas.getContext('2d')

    if (ctx) {
      // Create a linear gradient
      const gradient = ctx.createLinearGradient(0, 0, gradientCanvas.width, 0)

      // Add color stops
      colourStops.forEach((colourStop) =>
        gradient.addColorStop(colourStop.stop, colourStop.colour)
      )

      // Set the fill style and draw a rectangle
      ctx.fillStyle = gradient
      ctx.fillRect(0, 0, gradientCanvas.width, gradientCanvas.height)
    }
    return PIXI.Texture.from(gradientCanvas)
  }
}
